import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../api/axios";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount: restore session from stored JWT token
  useEffect(() => {
    const token = localStorage.getItem("parsian_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api.get("/auth/me")
      .then((res) => setUser(res.data.user))
      .catch(() => {
        localStorage.removeItem("parsian_token");
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  // Login with email + password → store token → set user
  const login = useCallback(async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    localStorage.setItem("parsian_token", res.data.token);
    setUser(res.data.user);
    return res.data.user;
  }, []);

  // Called after OTP verified — token + user come from verify-signup response
  const loginWithToken = useCallback((token, user) => {
    localStorage.setItem("parsian_token", token);
    setUser(user);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("parsian_token");
    setUser(null);
  }, []);

  // Safe updateUser — works even if previous user is null
  const updateUser = useCallback((updates) => {
    setUser((prev) => {
      if (!prev) return updates;
      return { ...prev, ...updates };
    });
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithToken, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
